// RevenueForecastCalculator.js

import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Slider,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { Card, CardContent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { Table as AntTable, ConfigProvider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue, blueGrey, red, green } from '@mui/material/colors';
import { ColorPicker } from 'antd';

// Theme and styled components
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
      light: blue[300],
      dark: blue[900],
    },
    secondary: {
      main: blueGrey[600],
      light: blueGrey[400],
      dark: blueGrey[800],
    },
    background: {
      default: lightBlue[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 15,
          },
        },
      },
    },
  },
});

const GlassCard = muiStyled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: 30,
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.18)}`,
  padding: theme.spacing(4),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 12px 40px 0 ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const ColorButton = muiStyled(Button)(({ theme, color }) => ({
  color: theme.palette.getContrastText(color),
  backgroundColor: color,
  '&:hover': {
    backgroundColor: alpha(color, 0.9),
  },
  borderRadius: 30,
  padding: '10px 20px',
  fontWeight: 600,
}));

const StyledTextField = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${blue[500]};
  }
`;

const StyledSlider = styled(Slider)`
  color: ${blue[500]};
`;

const SectionSubtitle = styled(Typography)`
  font-size: 1.2rem;
  color: #4a4a4a;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const StyledTableContainer = styled('div')`
  .ant-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: rgb(75, 85, 99) !important;
    font-weight: 500;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    
    &:hover {
      background: #fafafa !important;
    }
    
    &.ant-table-column-has-sorters:hover {
      background: #fafafa !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: #fafafa;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5 !important;
  }

  @media (max-width: 767px) {
    .ant-table {
      font-size: 0.875rem;
    }
    
    .ant-table-thead > tr > th {
      padding: 8px 4px;
      white-space: nowrap;
    }
    
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

const RevenueForecastCalculator = () => {
  // State variables
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState({
    id: uuidv4(),
    name: "Plan 1",
    numberOfHighNetWorthParticipants: 15,
    avgExpectedRolloverSize: 250000,
    closeRate: 35,
    advisoryFeePercent: 1.25,
    crossSellOpportunities: 8,
    avgCrossSellValue: 150000,
    crossSellCloseRate: 25,
    includeInGraph: true,
    color: '#e74c3c',
  });

  // Revenue Forecast calculation functions
  const calculateRevenueForecast = (scenario) => {
    const rolloverOpportunities = scenario.numberOfHighNetWorthParticipants;
    const expectedRollovers = rolloverOpportunities * (scenario.closeRate / 100);
    const totalRolloverAssets = expectedRollovers * scenario.avgExpectedRolloverSize;
    const rolloverRevenue = totalRolloverAssets * (scenario.advisoryFeePercent / 100);
    
    const crossSellOpportunities = scenario.crossSellOpportunities;
    const expectedCrossSells = crossSellOpportunities * (scenario.crossSellCloseRate / 100);
    const totalCrossSellAssets = expectedCrossSells * scenario.avgCrossSellValue;
    const crossSellRevenue = totalCrossSellAssets * (scenario.advisoryFeePercent / 100);
    
    const totalRevenuePotential = rolloverRevenue + crossSellRevenue;
    const totalAssets = totalRolloverAssets + totalCrossSellAssets;
    const totalOpportunities = rolloverOpportunities + crossSellOpportunities;
    const totalExpectedCloses = expectedRollovers + expectedCrossSells;
    const overallCloseRate = totalOpportunities > 0 ? (totalExpectedCloses / totalOpportunities) * 100 : 0;
    
    // Generate rationale text
    const rationale = generateRationale(scenario, {
      rolloverRevenue,
      crossSellRevenue,
      totalRevenuePotential,
      expectedRollovers,
      expectedCrossSells,
      totalAssets,
      overallCloseRate,
    });
    
    return {
      rolloverOpportunities,
      expectedRollovers,
      totalRolloverAssets,
      rolloverRevenue,
      crossSellOpportunities,
      expectedCrossSells,
      totalCrossSellAssets,
      crossSellRevenue,
      totalRevenuePotential,
      totalAssets,
      overallCloseRate,
      rationale,
    };
  };

  const generateRationale = (scenario, analysis) => {
    const rationale = [];
    
    rationale.push(`Revenue Opportunity Analysis for ${scenario.name}:`);
    rationale.push(`• Rollover Potential: ${analysis.expectedRollovers.toFixed(1)} expected rollovers from ${scenario.numberOfHighNetWorthParticipants} high-net-worth participants`);
    rationale.push(`• Rollover Revenue: $${formatNumber(analysis.rolloverRevenue.toFixed(2))} annually at ${scenario.advisoryFeePercent}% fee`);
    rationale.push(`• Cross-sell Potential: ${analysis.expectedCrossSells.toFixed(1)} expected cross-sells from ${scenario.crossSellOpportunities} opportunities`);
    rationale.push(`• Cross-sell Revenue: $${formatNumber(analysis.crossSellRevenue.toFixed(2))} annually`);
    rationale.push(`• Total Revenue Potential: $${formatNumber(analysis.totalRevenuePotential.toFixed(2))} annually`);
    rationale.push(`• Total Assets Under Management: $${formatNumber(analysis.totalAssets.toFixed(2))}`);
    rationale.push(`• Overall Close Rate: ${analysis.overallCloseRate.toFixed(1)}%`);
    
    if (analysis.totalRevenuePotential > 100000) {
      rationale.push(`\nStrategic Recommendation: High revenue potential justifies dedicated relationship management and enhanced service offerings.`);
    } else if (analysis.totalRevenuePotential > 50000) {
      rationale.push(`\nStrategic Recommendation: Moderate revenue potential supports targeted outreach and relationship building initiatives.`);
    } else {
      rationale.push(`\nStrategic Recommendation: Consider improving close rates or expanding participant base to maximize revenue potential.`);
    }
    
    return rationale.join('\n');
  };

  const generateRevenueForecastData = () => {
    const scenariosToAnalyze = scenarios.length > 0 ? scenarios : [currentScenario];
    
    return scenariosToAnalyze.map(scenario => {
      const analysis = calculateRevenueForecast(scenario);
      return {
        name: scenario.name,
        rolloverRevenue: analysis.rolloverRevenue,
        crossSellRevenue: analysis.crossSellRevenue,
        totalRevenuePotential: analysis.totalRevenuePotential,
        totalAssets: analysis.totalAssets,
        expectedRollovers: analysis.expectedRollovers,
        expectedCrossSells: analysis.expectedCrossSells,
        overallCloseRate: analysis.overallCloseRate,
        rationale: analysis.rationale,
        color: scenario.color,
      };
    });
  };

  // Scenario management functions
  const handleAddOrUpdateScenario = () => {
    const existingScenarioIndex = scenarios.findIndex(s => s.id === currentScenario.id);
    
    if (existingScenarioIndex !== -1) {
      const updatedScenarios = [...scenarios];
      updatedScenarios[existingScenarioIndex] = { ...currentScenario };
      setScenarios(updatedScenarios);
    } else {
      const newScenarioName = `Plan ${scenarios.length + 1}`;
      setScenarios([...scenarios, { ...currentScenario, name: newScenarioName }]);
    }

    setCurrentScenario({
      id: uuidv4(),
      name: `Plan ${scenarios.length + 2}`,
      numberOfHighNetWorthParticipants: 15,
      avgExpectedRolloverSize: 250000,
      closeRate: 35,
      advisoryFeePercent: 1.25,
      crossSellOpportunities: 8,
      avgCrossSellValue: 150000,
      crossSellCloseRate: 25,
      includeInGraph: true,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`,
    });
  };

  const handleEditScenario = (id) => {
    const scenarioToEdit = scenarios.find(scenario => scenario.id === id);
    setCurrentScenario(scenarioToEdit);
  };

  const handleRemoveScenario = (id) => {
    const updatedScenarios = scenarios.filter((scenario) => scenario.id !== id);
    const renamedScenarios = updatedScenarios.map((scenario, index) => ({
      ...scenario,
      name: `Plan ${index + 1}`
    }));
    setScenarios(renamedScenarios);
    
    setCurrentScenario(prev => ({
      ...prev,
      name: `Plan ${renamedScenarios.length + 1}`
    }));
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Table state and handling
  const [sortedInfo, setSortedInfo] = useState({});
  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  // Revenue Forecast table columns
  const columns = [
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
    },
    {
      title: 'Rollover Revenue',
      dataIndex: 'rolloverRevenue',
      key: 'rolloverRevenue',
      sorter: (a, b) => (a.rolloverRevenue || 0) - (b.rolloverRevenue || 0),
      sortOrder: sortedInfo.columnKey === 'rolloverRevenue' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Cross-sell Revenue',
      dataIndex: 'crossSellRevenue',
      key: 'crossSellRevenue',
      sorter: (a, b) => (a.crossSellRevenue || 0) - (b.crossSellRevenue || 0),
      sortOrder: sortedInfo.columnKey === 'crossSellRevenue' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Total Revenue Potential',
      dataIndex: 'totalRevenuePotential',
      key: 'totalRevenuePotential',
      sorter: (a, b) => (a.totalRevenuePotential || 0) - (b.totalRevenuePotential || 0),
      sortOrder: sortedInfo.columnKey === 'totalRevenuePotential' && sortedInfo.order,
      render: (value) => (
        <span style={{ color: value >= 100000 ? green[600] : value >= 50000 ? blue[600] : red[600], fontWeight: 'bold' }}>
          ${formatNumber(value.toFixed(2))}
        </span>
      ),
    },
    {
      title: 'Total Assets',
      dataIndex: 'totalAssets',
      key: 'totalAssets',
      sorter: (a, b) => (a.totalAssets || 0) - (b.totalAssets || 0),
      sortOrder: sortedInfo.columnKey === 'totalAssets' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Expected Rollovers',
      dataIndex: 'expectedRollovers',
      key: 'expectedRollovers',
      sorter: (a, b) => (a.expectedRollovers || 0) - (b.expectedRollovers || 0),
      sortOrder: sortedInfo.columnKey === 'expectedRollovers' && sortedInfo.order,
      render: (value) => value.toFixed(1),
    },
    {
      title: 'Expected Cross-sells',
      dataIndex: 'expectedCrossSells',
      key: 'expectedCrossSells',
      sorter: (a, b) => (a.expectedCrossSells || 0) - (b.expectedCrossSells || 0),
      sortOrder: sortedInfo.columnKey === 'expectedCrossSells' && sortedInfo.order,
      render: (value) => value.toFixed(1),
    },
    {
      title: 'Overall Close Rate',
      dataIndex: 'overallCloseRate',
      key: 'overallCloseRate',
      sorter: (a, b) => (a.overallCloseRate || 0) - (b.overallCloseRate || 0),
      sortOrder: sortedInfo.columnKey === 'overallCloseRate' && sortedInfo.order,
      render: (value) => `${value.toFixed(1)}%`,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ padding: '24px' }}
      >
        <Grid container spacing={4}>
          {/* Input Section */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Define Revenue Forecast
                </SectionSubtitle>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Plan Name</Typography>
                  <StyledTextField
                    value={currentScenario.name}
                    onChange={(e) =>
                      setCurrentScenario({ ...currentScenario, name: e.target.value })
                    }
                    placeholder="Plan Name"
                  />
                </Box>
                <Typography gutterBottom>
                  High-Net-Worth Participants: {currentScenario.numberOfHighNetWorthParticipants}
                </Typography>
                <StyledSlider
                  value={currentScenario.numberOfHighNetWorthParticipants}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, numberOfHighNetWorthParticipants: val })
                  }
                  aria-labelledby="high-net-worth-participants"
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={100}
                />
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.avgExpectedRolloverSize}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, avgExpectedRolloverSize: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Average Expected Rollover Size"
                  style={{ marginBottom: '16px' }}
                />
                <Typography gutterBottom>
                  Rollover Close Rate: {currentScenario.closeRate}%
                </Typography>
                <StyledSlider
                  value={currentScenario.closeRate}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, closeRate: val })
                  }
                  aria-labelledby="close-rate"
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={100}
                />
                <Typography gutterBottom>
                  Advisory Fee Percentage: {currentScenario.advisoryFeePercent}%
                </Typography>
                <StyledSlider
                  value={currentScenario.advisoryFeePercent}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, advisoryFeePercent: val })
                  }
                  aria-labelledby="advisory-fee-percent"
                  valueLabelDisplay="auto"
                  step={0.05}
                  min={0}
                  max={3}
                />
                <Typography gutterBottom>
                  Cross-sell Opportunities: {currentScenario.crossSellOpportunities}
                </Typography>
                <StyledSlider
                  value={currentScenario.crossSellOpportunities}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, crossSellOpportunities: val })
                  }
                  aria-labelledby="cross-sell-opportunities"
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={50}
                />
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.avgCrossSellValue}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, avgCrossSellValue: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Average Cross-sell Value"
                  style={{ marginBottom: '16px' }}
                />
                <Typography gutterBottom>
                  Cross-sell Close Rate: {currentScenario.crossSellCloseRate}%
                </Typography>
                <StyledSlider
                  value={currentScenario.crossSellCloseRate}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, crossSellCloseRate: val })
                  }
                  aria-labelledby="cross-sell-close-rate"
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={100}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentScenario.includeInGraph}
                        onChange={(e) =>
                          setCurrentScenario({ ...currentScenario, includeInGraph: e.target.checked })
                        }
                        name="includeInGraph"
                        color="primary"
                      />
                    }
                    label="Include in Graph"
                  />
                  <ColorPicker
                    value={currentScenario.color}
                    onChange={(color) => setCurrentScenario({ ...currentScenario, color: color.toHexString() })}
                    trigger="hover"
                    disabledAlpha
                    presets={[
                      {
                        label: 'Recommended',
                        colors: [
                          '#4a90e2', '#50C878', '#FF6B6B', '#9B59B6',
                          '#34495E', '#2ECC71', '#E74C3C', '#8E44AD'
                        ],
                      },
                    ]}
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <ColorButton
                    variant="contained"
                    onClick={handleAddOrUpdateScenario}
                    startIcon={<AddIcon />}
                    color={currentScenario.color}
                    fullWidth
                  >
                    {scenarios.some(s => s.id === currentScenario.id) ? 'Update Plan' : 'Add Plan'}
                  </ColorButton>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Scenario List */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Revenue Plans
                </SectionSubtitle>
                {scenarios.length > 0 ? (
                  scenarios.map((scenario) => {
                    const analysis = calculateRevenueForecast(scenario);
                    return (
                      <Box key={scenario.id} sx={{ mb: 2, position: 'relative' }}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 2,
                            borderRadius: '10px',
                            background: alpha(scenario.color, 0.1),
                            border: `1px solid ${alpha(scenario.color, 0.3)}`,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: scenario.color }}>
                            {scenario.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Revenue Potential: <span style={{ color: analysis.totalRevenuePotential >= 100000 ? green[600] : analysis.totalRevenuePotential >= 50000 ? blue[600] : red[600], fontWeight: 'bold' }}>
                              ${formatNumber(analysis.totalRevenuePotential.toFixed(2))}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Expected Rollovers: <span style={{ fontWeight: 'bold' }}>
                              {analysis.expectedRollovers.toFixed(1)}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Expected Cross-sells: <span style={{ fontWeight: 'bold' }}>
                              {analysis.expectedCrossSells.toFixed(1)}
                            </span>
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={scenario.includeInGraph}
                                  onChange={(e) => {
                                    const newScenarios = scenarios.map((s) =>
                                      s.id === scenario.id ? { ...s, includeInGraph: e.target.checked } : s
                                    );
                                    setScenarios(newScenarios);
                                  }}
                                  name="includeInGraph"
                                  color="primary"
                                />
                              }
                              label="Include in Graph"
                            />
                            <Box>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditScenario(scenario.id)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                onClick={() => handleRemoveScenario(scenario.id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body1">No saved plans. Create one to get started!</Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Revenue Breakdown Chart */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Revenue Breakdown
                </SectionSubtitle>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={generateRevenueForecastData()}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip 
                      formatter={(value, name) => [`$${formatNumber(value.toFixed(2))}`, name]}
                    />
                    <Legend />
                    <Bar dataKey="rolloverRevenue" fill="#4a90e2" name="Rollover Revenue" />
                    <Bar dataKey="crossSellRevenue" fill="#50c878" name="Cross-sell Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Strategy Rationale */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Strategy Rationale
                </SectionSubtitle>
                {generateRevenueForecastData().length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 2, backgroundColor: alpha(theme.palette.background.paper, 0.5), borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                      {generateRevenueForecastData()[0].rationale}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1">Add a plan to see strategy rationale.</Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Data Table */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants} style={{ marginBottom: '2rem' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <SectionSubtitle variant="h6" sx={{ color: theme.palette.secondary.main }}>
                    Revenue Forecast Analysis
                  </SectionSubtitle>
                  <Tooltip title="Download Excel">
                    <Button
                      onClick={() => {
                        const worksheet = XLSX.utils.json_to_sheet(generateRevenueForecastData());
                        const workbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue Forecast Analysis");
                        XLSX.writeFile(workbook, "revenue_forecast_analysis.xlsx");
                      }}
                      startIcon={<DownloadOutlined />}
                      sx={{
                        color: theme.palette.secondary.main,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: 'transparent',
                        },
                        transition: 'all 0.3s ease',
                        textTransform: 'none',
                        fontSize: '14px',
                      }}
                    >
                      Download Excel
                    </Button>
                  </Tooltip>
                </Box>
                <ConfigProvider
                  theme={{
                    token: {
                      fontFamily: "'Poppins', sans-serif",
                      borderRadius: 12,
                      colorBgContainer: '#ffffff',
                      colorBorderSecondary: '#f0f0f0',
                      fontSize: 14,
                    },
                    components: {
                      Table: {
                        headerBg: '#ffffff',
                        headerColor: 'rgb(75, 85, 99)',
                        rowHoverBg: '#f5f5f5',
                        headerSortHoverBg: '#fafafa',
                        headerSortActiveBg: '#fafafa',
                        borderColor: '#f0f0f0',
                        rowExpandedBg: '#fafafa',
                      },
                    },
                  }}
                >
                  <StyledTableContainer>
                    <AntTable
                      columns={columns}
                      dataSource={generateRevenueForecastData()}
                      rowKey="name"
                      onChange={handleTableChange}
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        style: { marginTop: '16px' }
                      }}
                      scroll={{ x: 'max-content' }}
                    />
                  </StyledTableContainer>
                </ConfigProvider>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </motion.div>
    </ThemeProvider>
  );
};

export default RevenueForecastCalculator; 